import type { ElvaTheme } from './themes';
import { DEFAULT_THEME_ID, getThemeById, THEMES } from './themes';

function getDefaultTheme(): ElvaTheme {
  const theme = getThemeById(DEFAULT_THEME_ID);
  if (!theme) throw new Error('Default Elva theme "neue" is required');
  return theme;
}

const DEFAULT_THEME = getDefaultTheme();

export const THEME_STORAGE_KEY = 'elva-theme';
export const THEME_CHANGE_EVENT = 'elva-theme-change';

export function notifyThemeStorageChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
}

export type BgOverlay = 'none' | 'grid' | 'dots' | 'diagonal' | 'glow';

export interface ApplyThemeOptions {
  bg?: BgOverlay;
}

function hexToRgba(hex: string, a: number): string {
  if (!hex.startsWith('#')) return hex;
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${String((n >> 16) & 255)}, ${String((n >> 8) & 255)}, ${String(n & 255)}, ${String(a)})`;
}

export { hexToRgba };

function overlayLineColor(dark: boolean): string {
  return dark ? 'rgba(255,255,255,0.06)' : 'rgba(22,30,46,0.05)';
}

function overlayDotColor(dark: boolean): string {
  return dark ? 'rgba(255,255,255,0.07)' : 'rgba(22,30,46,0.06)';
}

function buildOverlayLayer(overlay: BgOverlay, theme: ElvaTheme): { layer: string; size: string } | null {
  const lineC = overlayLineColor(theme.dark);
  const dotC = overlayDotColor(theme.dark);

  switch (overlay) {
    case 'none':
      return null;
    case 'grid':
      return {
        layer: `linear-gradient(${lineC} 1px, transparent 1px), linear-gradient(90deg, ${lineC} 1px, transparent 1px)`,
        size: '54px 54px',
      };
    case 'dots':
      return {
        layer: `radial-gradient(${dotC} 1.7px, transparent 1.8px)`,
        size: '24px 24px',
      };
    case 'diagonal':
      return {
        layer: `repeating-linear-gradient(45deg, ${lineC} 0 1px, transparent 1px 14px)`,
        size: 'auto',
      };
    case 'glow': {
      const a3 = hexToRgba(theme.accent[0], theme.dark ? 0.35 : 0.25);
      const a4 = hexToRgba(theme.accent[1], theme.dark ? 0.3 : 0.2);
      const a5 = hexToRgba(theme.accent[2], theme.dark ? 0.28 : 0.18);
      return {
        layer: `radial-gradient(ellipse 80% 60% at 20% 20%, ${a3}, transparent 70%), radial-gradient(ellipse 70% 55% at 80% 30%, ${a4}, transparent 70%), radial-gradient(ellipse 75% 50% at 50% 90%, ${a5}, transparent 70%)`,
        size: '100% 100%',
      };
    }
    default:
      return null;
  }
}

function composeBodyBackground(
  theme: ElvaTheme,
  options?: ApplyThemeOptions,
): { background: string; backgroundSize: string } {
  const base = theme.bodyBackground ?? theme.page;
  const baseSize = theme.bodyBackgroundSize ?? '100% 100%';
  const overlay = options?.bg ?? 'none';

  if (overlay === 'none') {
    return { background: base, backgroundSize: baseSize };
  }

  const overlayDef = buildOverlayLayer(overlay, theme);
  if (!overlayDef) {
    return { background: base, backgroundSize: baseSize };
  }

  return {
    background: `${overlayDef.layer}, ${base}`,
    backgroundSize:
      overlayDef.size === 'auto'
        ? `${overlayDef.size}, ${baseSize}`
        : `${overlayDef.size}, ${baseSize}`,
  };
}

function resolveThemeId(id: string | null | undefined, fallback = DEFAULT_THEME_ID): string {
  const trimmed = id?.trim();
  if (trimmed && getThemeById(trimmed)) return trimmed;
  if (getThemeById(fallback)) return fallback;
  return DEFAULT_THEME_ID;
}

/** Read and validate the saved theme id; repairs stale localStorage values. */
export function resolvePersistedThemeId(
  fallback = DEFAULT_THEME_ID,
  storageKey = THEME_STORAGE_KEY,
): string {
  const resolvedFallback = resolveThemeId(fallback, DEFAULT_THEME_ID);
  if (typeof window === 'undefined') return resolvedFallback;

  try {
    const stored = localStorage.getItem(storageKey);
    const legacyStored =
      storageKey === THEME_STORAGE_KEY ? null : localStorage.getItem(THEME_STORAGE_KEY);
    const resolved = resolveThemeId(stored ?? legacyStored, resolvedFallback);
    if (stored != null && stored !== resolved) {
      localStorage.setItem(storageKey, resolved);
    }
    if (stored == null && legacyStored != null) {
      localStorage.setItem(storageKey, resolved);
    }
    return resolved;
  } catch {
    return resolvedFallback;
  }
}

export function getPersistedThemeId(
  fallback = DEFAULT_THEME_ID,
  storageKey = THEME_STORAGE_KEY,
): string {
  return resolvePersistedThemeId(fallback, storageKey);
}

export function persistThemeId(
  id: string,
  fallback = DEFAULT_THEME_ID,
  storageKey = THEME_STORAGE_KEY,
): string {
  const resolved = resolveThemeId(id, fallback);
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(storageKey, resolved);
      if (storageKey !== THEME_STORAGE_KEY) {
        localStorage.setItem(THEME_STORAGE_KEY, resolved);
      }
      notifyThemeStorageChanged();
    } catch {
      // Ignore blocked storage (private mode, etc.).
    }
  }
  return resolved;
}

interface ThemeCssSnapshot {
  id: string;
  dark: boolean;
  vars: Record<string, string>;
  removeShadowVars: boolean;
  bodyBackground: string;
  bodyBackgroundSize: string;
}

function buildThemeCssSnapshot(theme: ElvaTheme, options?: ApplyThemeOptions): ThemeCssSnapshot {
  const tintA = theme.dark ? 0.22 : 0.16;
  const goldA = theme.dark ? 0.24 : 0.18;
  const hasGradientBg = Boolean(theme.bodyBackground);
  const vars: Record<string, string> = {};

  vars['--cream-0'] = theme.page;
  vars['--page'] = hasGradientBg ? 'transparent' : theme.page;
  vars['--cream-1'] = theme.surface;
  vars['--cream-2'] = theme.line;
  vars['--cream-shadow'] = theme.shadowLine;
  vars['--surface-mute'] = theme.surfaceMute;

  theme.ink.forEach((c, i) => {
    vars[`--ink-${String(i)}`] = c;
  });

  vars['--sage-0'] = theme.dark ? theme.accent[0] : '#1D1D1F';
  vars['--sage-1'] = theme.ink[1];
  vars['--sage-2'] = theme.dark ? hexToRgba(theme.ink[4], 0.45) : theme.line;
  vars['--sage-3'] = hexToRgba(theme.accent[0], theme.dark ? 0.18 : 0.1);

  vars['--ai-3'] = theme.accent[0];
  vars['--ai-4'] = theme.accent[1];
  vars['--ai-5'] = theme.accent[2];
  vars['--ai-1'] = hexToRgba(theme.accent[0], theme.dark ? 0.55 : 0.35);
  vars['--ai-2'] = hexToRgba(theme.accent[1], theme.dark ? 0.5 : 0.32);
  vars['--ai-g1'] = hexToRgba(theme.accent[0], theme.dark ? 0.35 : 0.22);
  vars['--ai-g2'] = hexToRgba(theme.accent[1], theme.dark ? 0.32 : 0.2);
  vars['--ai-tint'] = hexToRgba(theme.accent[0], tintA);

  vars['--success'] = theme.success;
  vars['--success-tint'] = hexToRgba(theme.success, tintA);
  vars['--gold'] = theme.gold;
  vars['--gold-tint'] = hexToRgba(theme.gold, goldA);
  vars['--terracotta'] = theme.terracotta;
  vars['--terracotta-tint'] = hexToRgba(theme.terracotta, tintA);
  vars['--info'] = theme.info;
  vars['--info-tint'] = hexToRgba(theme.info, tintA);

  vars['--grad-spectrum'] = `linear-gradient(100deg, ${theme.accent[0]}, ${theme.accent[1]} 46%, ${theme.accent[2]} 86%)`;
  vars['--grad-ink'] = `linear-gradient(160deg, ${theme.ink[1]}, ${theme.dark ? theme.accent[0] : theme.ink[0]})`;

  // §16 theme-token layer (Calendar color system) — discrete theme-specific tokens.
  // Headline gradient stops, selected/focus/workspace states. Semantic + structural
  // tokens stay separate (set above / in elva.css :root); these change per palette.
  vars['--headline-grad-start'] = theme.accent[0];
  vars['--headline-grad-mid'] = theme.accent[1];
  vars['--headline-grad-end'] = theme.accent[2];
  vars['--selected-border'] = theme.accent[0];
  vars['--selected-soft'] = hexToRgba(theme.accent[0], theme.dark ? 0.22 : 0.1);
  vars['--focus'] = theme.accent[1];
  vars['--workspace-selected'] = hexToRgba(theme.accent[0], theme.dark ? 0.2 : 0.1);

  // Glossy / frosted sidebar (light themes only). Dark keeps its current solid look
  // (the handoff doc explicitly leaves the dark sidebar unchanged this pass).
  if (theme.dark) {
    vars['--sidebar-bg'] = theme.surface;
    vars['--sidebar-backdrop'] = 'none';
    vars['--sidebar-border'] = theme.line;
    vars['--sidebar-shadow'] = 'none';
    vars['--nav-active-bg'] = theme.accent[0];
    vars['--nav-active-shadow'] = 'none';
    vars['--nav-active-border'] = 'transparent';
    vars['--nav-hover-bg'] = theme.surfaceMute;
    vars['--nav-hover-border'] = 'transparent';
    vars['--ws-card-bg'] = theme.page;
    vars['--ws-card-border'] = theme.line;
    vars['--ws-card-shadow'] = 'none';
    vars['--ws-card-backdrop'] = 'none';
    vars['--sidebar-badge'] = theme.terracotta;
    vars['--backbtn-bg'] = theme.surfaceMute;
    vars['--backbtn-border'] = theme.line;
    // dark sidebar keeps the normal light-on-dark ink ramp
    vars['--sidebar-ink-strong'] = theme.ink[0];
    vars['--sidebar-ink'] = theme.ink[1];
    vars['--sidebar-muted'] = theme.ink[3];
    vars['--sidebar-icon'] = theme.ink[3];
  } else {
    // FLAT SURFACE sidebar (design system rev 1). The glass is gone: the sidebar is
    // --surface-card on the --surface-app ground, separated by a single hairline.
    // Brand-indigo is the interaction colour — the active row is a flat indigo pill
    // with the one coloured shadow in the chrome (--shadow-nav-active).
    vars['--sidebar-bg'] = theme.surface; // --surface-card
    vars['--sidebar-backdrop'] = 'none';
    vars['--sidebar-border'] = theme.line; // --border-default
    vars['--sidebar-shadow'] = '0 1px 3px rgba(36,41,54,0.04)'; // --shadow-card
    // active item = a flat brand-indigo pill, white label, one coloured shadow.
    vars['--nav-active-bg'] = theme.accent[0]; // --brand-indigo
    vars['--nav-active-shadow'] = '0 4px 12px rgba(76,91,212,0.28)'; // --shadow-nav-active
    vars['--nav-active-border'] = 'transparent';
    // hover = the brand-indigo tint, no movement.
    vars['--nav-hover-bg'] = hexToRgba(theme.accent[0], 0.08); // ≈ --brand-indigo-tint
    vars['--nav-hover-border'] = 'transparent';
    // workspace card = a sunken panel, one hairline.
    vars['--ws-card-bg'] = theme.surfaceMute; // --surface-sunken
    vars['--ws-card-border'] = theme.line;
    vars['--ws-card-shadow'] = 'none';
    vars['--ws-card-backdrop'] = 'none';
    vars['--sidebar-badge'] = theme.terracotta; // --alert-red
    vars['--backbtn-bg'] = theme.surfaceMute;
    vars['--backbtn-border'] = theme.line;
    // cool-neutral text/icons: text-primary / text-secondary / text-muted / icon-default.
    vars['--sidebar-ink-strong'] = theme.ink[0];
    vars['--sidebar-ink'] = theme.ink[1];
    vars['--sidebar-muted'] = theme.ink[3];
    vars['--sidebar-icon'] = '#8A92A6'; // --icon-default
  }

  // Glossy top controls + distinct calendar-header band (handoff §2–§14).
  // Light = translucent glass; dark = restrained (dense) treatment.
  if (theme.dark) {
    vars['--control-bg'] = theme.surface;
    vars['--control-bg-hover'] = theme.surfaceMute;
    vars['--control-border'] = theme.line;
    vars['--control-highlight'] = 'rgba(255,255,255,0.07)';
    vars['--control-bottom-shade'] = 'rgba(0,0,0,0.28)';
    vars['--control-shadow'] = 'rgba(0,0,0,0.28)';
    vars['--control-text'] = theme.ink[1];
    vars['--control-gloss'] = 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 60%)';
    vars['--control-blur'] = 'none';
    vars['--segment-active-bg'] = theme.surfaceMute;
    vars['--segment-active-border'] = theme.accent[0];
    vars['--segment-active-text'] = theme.ink[0];
    vars['--segment-active-hl'] = 'rgba(255,255,255,0.06)';
    vars['--segment-active-shadow'] = 'rgba(0,0,0,0.32)';
    vars['--segment-active-ring'] = 'transparent';
    vars['--calendar-header-bg'] = theme.page;
    vars['--calendar-header-border'] = theme.line;
    vars['--calendar-header-bottom-line'] = theme.line;
    vars['--calendar-header-gloss'] = 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0) 48%)';
    vars['--calendar-header-inner-hl'] = 'rgba(255,255,255,0.05)';
    vars['--calendar-header-ink'] = theme.ink[0];
    vars['--calendar-header-metric'] = theme.ink[2];
    vars['--calendar-header-muted'] = theme.ink[3];
    vars['--calendar-header-faint'] = theme.ink[4];
    vars['--calendar-header-line'] = theme.line;
    vars['--current-day-bg'] = hexToRgba(theme.accent[0], 0.12);
    vars['--current-day-border'] = theme.accent[0];
    vars['--current-day-text'] = theme.accent[0];
    // dark stays restrained: solid KPI surface + only a whisper of inner highlight
    vars['--kpi-bg'] = theme.surface;
    vars['--kpi-highlight'] = 'rgba(255,255,255,0.04)';
    vars['--kpi-shadow'] = 'rgba(0,0,0,0.25)';
    vars['--appointment-highlight'] = 'rgba(255,255,255,0.05)';
    vars['--appointment-shadow'] = 'rgba(0,0,0,0.22)';
  } else {
    // Design system rev 1: FLAT controls — no gloss, no blur. A control is a
    // --surface-header chip with one hairline; the active segment is a flat white
    // pill with a brand-indigo edge.
    vars['--control-bg'] = theme.surfaceMute; // --surface-sunken
    vars['--control-bg-hover'] = theme.page; // --surface-app
    vars['--control-border'] = theme.line; // --border-default
    vars['--control-highlight'] = 'transparent';
    vars['--control-bottom-shade'] = 'transparent';
    vars['--control-shadow'] = 'rgba(36,41,54,0.06)';
    vars['--control-text'] = theme.ink[1]; // --text-secondary
    vars['--control-gloss'] = 'none';
    vars['--control-blur'] = 'none';
    vars['--segment-active-bg'] = theme.surface; // flat white
    vars['--segment-active-border'] = theme.accent[0]; // --brand-indigo
    vars['--segment-active-text'] = theme.ink[0]; // --text-primary
    vars['--segment-active-hl'] = 'transparent';
    vars['--segment-active-shadow'] = 'rgba(76,91,212,0.14)';
    vars['--segment-active-ring'] = hexToRgba(theme.accent[0], 0.26);
    // header band = a FLAT brand-indigo toolbar (no gloss gradient), white type.
    vars['--calendar-header-bg'] = theme.accent[0]; // --brand-indigo
    vars['--calendar-header-border'] = 'rgba(255,255,255,0.16)';
    vars['--calendar-header-bottom-line'] = hexToRgba(theme.accent[0], 1);
    vars['--calendar-header-gloss'] = 'none';
    vars['--calendar-header-inner-hl'] = 'rgba(255,255,255,0.20)';
    vars['--calendar-header-ink'] = '#FFFFFF';
    vars['--calendar-header-metric'] = 'rgba(255,255,255,0.86)';
    vars['--calendar-header-muted'] = 'rgba(255,255,255,0.70)';
    vars['--calendar-header-faint'] = 'rgba(255,255,255,0.42)';
    vars['--calendar-header-line'] = 'rgba(255,255,255,0.16)';
    // current-day (week header) = a lighter indigo panel on the flat band.
    vars['--current-day-bg'] = hexToRgba(theme.accent[1], 1);
    vars['--current-day-border'] = 'rgba(255,255,255,0.55)';
    vars['--current-day-text'] = '#FFFFFF';
    // KPI cards stay clean flat white — colour lives in the objects inside them.
    vars['--kpi-bg'] = theme.surface;
    vars['--kpi-highlight'] = 'transparent';
    vars['--kpi-shadow'] = 'rgba(36,41,54,0.06)';
    vars['--appointment-highlight'] = 'transparent';
    vars['--appointment-shadow'] = 'rgba(36,41,54,0.06)';
  }

  if (theme.dark) {
    vars['--sh-1'] = '0 1px 3px rgba(0, 0, 0, 0.35)';
    vars['--sh-2'] = '0 18px 44px -28px rgba(0, 0, 0, 0.55), 0 1px 3px rgba(0, 0, 0, 0.2)';
    vars['--sh-3'] = '0 24px 60px -24px rgba(0, 0, 0, 0.65), 0 2px 6px rgba(0, 0, 0, 0.25)';
    vars['--sh-btn'] = '0 10px 22px -12px rgba(0, 0, 0, 0.55)';
  }

  const { background, backgroundSize } = composeBodyBackground(theme, options);

  return {
    id: theme.id,
    dark: theme.dark,
    vars,
    removeShadowVars: !theme.dark,
    bodyBackground: background,
    bodyBackgroundSize: backgroundSize,
  };
}

function applyThemeSnapshot(snapshot: ThemeCssSnapshot): void {
  if (typeof document === 'undefined') return;

  const rootStyle = document.documentElement.style;
  for (const [name, value] of Object.entries(snapshot.vars)) {
    rootStyle.setProperty(name, value);
  }

  if (snapshot.removeShadowVars) {
    rootStyle.removeProperty('--sh-1');
    rootStyle.removeProperty('--sh-2');
    rootStyle.removeProperty('--sh-3');
    rootStyle.removeProperty('--sh-btn');
  }

  document.body.style.background = snapshot.bodyBackground;
  document.body.style.backgroundSize = snapshot.bodyBackgroundSize;
  document.documentElement.style.colorScheme = snapshot.dark ? 'dark' : 'light';
  document.documentElement.dataset.elvaTheme = snapshot.id;
}

const THEME_CSS_SNAPSHOTS = Object.fromEntries(
  THEMES.map((theme) => [theme.id, buildThemeCssSnapshot(theme)]),
) as Record<string, ThemeCssSnapshot>;

/**
 * A theme's token map, for scoping a theme to a subtree (e.g. an inline `style`) instead of
 * the root. Light themes omit the shadow vars, so they fall back to the elva.css defaults.
 */
export function getThemeCssVars(id: string): Record<string, string> {
  const snapshot = THEME_CSS_SNAPSHOTS[resolveThemeId(id)] ?? THEME_CSS_SNAPSHOTS[DEFAULT_THEME_ID];
  return { ...snapshot?.vars };
}

/** Apply before React mount to avoid flash of default tokens. */
export function initPersistedTheme(
  fallback = DEFAULT_THEME_ID,
  storageKey = THEME_STORAGE_KEY,
): void {
  applyTheme(resolvePersistedThemeId(fallback, storageKey));
}

/** Blocking inline script for SSR apps (Next.js) — runs before React hydration. */
export function buildThemeInitInlineScript(
  fallback = DEFAULT_THEME_ID,
  storageKey = THEME_STORAGE_KEY,
): string {
  const resolvedFallback = resolveThemeId(fallback, DEFAULT_THEME_ID);
  const snapshotsJson = JSON.stringify(THEME_CSS_SNAPSHOTS);
  return `(function(){try{var k=${JSON.stringify(storageKey)},lk=${JSON.stringify(THEME_STORAGE_KEY)},f=${JSON.stringify(resolvedFallback)},d=${JSON.stringify(DEFAULT_THEME_ID)},snap=${snapshotsJson},raw=localStorage.getItem(k),legacy=k===lk?null:localStorage.getItem(lk),id=snap[raw]?raw:(snap[legacy]?legacy:(snap[f]?f:d)),s=snap[id]||snap[d];if(!s||!document.documentElement)return;var r=document.documentElement.style;for(var p in s.vars)r.setProperty(p,s.vars[p]);if(s.removeShadowVars){r.removeProperty("--sh-1");r.removeProperty("--sh-2");r.removeProperty("--sh-3");r.removeProperty("--sh-btn");}if(document.body){document.body.style.background=s.bodyBackground;document.body.style.backgroundSize=s.bodyBackgroundSize;}document.documentElement.style.colorScheme=s.dark?"dark":"light";document.documentElement.dataset.elvaTheme=s.id;if(raw!==s.id)localStorage.setItem(k,s.id);if(k!==lk&&legacy!==s.id)localStorage.setItem(lk,s.id);}catch(e){}})();`;
}

export function applyTheme(th: string | ElvaTheme, options?: ApplyThemeOptions): void {
  if (typeof document === 'undefined') return;

  const theme: ElvaTheme =
    typeof th === 'string' ? (getThemeById(th) ?? DEFAULT_THEME) : th;

  applyThemeSnapshot(buildThemeCssSnapshot(theme, options));
}

export { THEMES };
